import React from 'react';
import { DataGrid } from '../common/datagrid/ResponsiveDataGrid';
import CustomToolbar from '../common/datagrid/CustomToolbar';
import CustomLoadingOverlay from '../common/datagrid/CustomLoadingOverlay';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Divider,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { getNumberComma } from '../../utils/numberUtils';

const getProjectTypeLabel = (value) => {
  if (value === 'M') return '유지보수';
  if (value === 'N' || value === 'P') return '프로젝트';
  return '-';
};

const MobileInfo = ({ label, value, align = 'left' }) => (
  <Stack spacing={0.25} sx={{ minWidth: 0, textAlign: align }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
      {value || '-'}
    </Typography>
  </Stack>
);

const ChanceList = ({
  apiRef,
  columns,
  rows,
  loading,
  handleRowClick,
  columnVisibilityModel,
}) => {
  const [pageSize, setPageSize] = React.useState(20);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const safeRows = rows || [];

  if (isMobile) {
    return (
      <Stack spacing={1.25} sx={{ width: '100%', minWidth: 0, pb: 2 }}>
        {loading && safeRows.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
            목록을 불러오는 중입니다.
          </Typography>
        )}

        {!loading && safeRows.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
            표시할 포캐스팅이 없습니다.
          </Typography>
        )}

        {safeRows.map((row, index) => {
          const rowId = row.id || row.prjUuid;
          const margin = row.totalMargin === undefined || row.totalMargin === null ? '-' : `${row.totalMargin}%`;

          return (
            <Card
              key={rowId || index}
              variant="outlined"
              sx={{
                borderRadius: 1.5,
                overflow: 'hidden',
                bgcolor: '#fff',
              }}
            >
              <CardActionArea onClick={() => handleRowClick?.({ id: rowId, row })}>
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Stack spacing={1.25}>
                    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 800, lineHeight: 1.35, wordBreak: 'break-word' }}
                        >
                          {row.cstNm || '사업명 없음'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                          {row.vendorCmpNm || row.partnerInc || '-'}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                        <Chip label={row.cstStatus || '-'} size="small" color="info" variant="outlined" />
                        <Chip label={getProjectTypeLabel(row.prjTyp)} size="small" variant="outlined" />
                      </Stack>
                    </Stack>

                    <Divider />

                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                        gap: 1,
                      }}
                    >
                      <MobileInfo label="프로젝트 번호" value={row.prjNo} />
                      <MobileInfo label="매출월" value={row.expDay} align="right" />
                      <MobileInfo label="부서" value={row.crtDptNm} />
                      <MobileInfo label="담당자" value={[row.crtNm, row.crtGradeNm].filter(Boolean).join(' ')} align="right" />
                    </Box>

                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                        gap: 1,
                        p: 1,
                        borderRadius: 1,
                        bgcolor: 'rgba(0,0,0,0.03)',
                      }}
                    >
                      <MobileInfo label="총매출액" value={getNumberComma(row.totalSales || 0)} />
                      <MobileInfo label="총매입액" value={getNumberComma(row.totalPurchase || 0)} align="right" />
                      <MobileInfo label="매출이익" value={getNumberComma(row.totalProfit || 0)} />
                      <MobileInfo label="이익률" value={margin} align="right" />
                    </Box>
                  </Stack>
                </CardContent>
              </CardActionArea>
            </Card>
          );
        })}
      </Stack>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 320px)', width: '100%', minWidth: 0 }}>
      <Box sx={{ flexGrow: 1, minWidth: 0, width: '100%' }}>
        <DataGrid
          apiRef={apiRef}
          rows={safeRows}
          columns={columns}
          components={{
            Toolbar: CustomToolbar,
            LoadingOverlay: CustomLoadingOverlay,
          }}
          componentsProps={{
            toolbar: { fileName: '포캐스팅_리스트' },
          }}
          density="compact"
          loading={loading}
          onRowClick={handleRowClick}
          pageSize={pageSize}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          rowsPerPageOptions={[5, 10, 20, 50, 100]}
          pagination
          columnVisibilityModel={columnVisibilityModel? columnVisibilityModel: []}
        />
      </Box>
    </Box>
  );
};

export default ChanceList;
